import { ApolloError } from '@apollo/client';
import { format, parseISO } from 'date-fns';
import { startCase } from 'lodash';
import { ComponentProps } from 'react';

import {
  RosterRequestDataDateChange,
  RosterRequestQuery,
  RosterRoomAssignment,
} from 'generated/graphql';

import AssignmentChanges from './DateChange/AssignmentChanges';
import DuplicateChangeRequestWarning from './DuplicateChangeRequestWarning';
import OverlappingStayWarning from './OverlappingStayWarning';

interface AssignmentsListProps {
  request: RosterRequestQuery['rosterRequest'];
  onRemoveAssignment: (assignmentId: string) => void;
  duplicateRequests?: ComponentProps<
    typeof DuplicateChangeRequestWarning
  >['duplicateRequests'];
  duplicateRequestsError?: ApolloError;
}

const AssignmentsList = ({
  request,
  duplicateRequests = [],
  duplicateRequestsError,
}: AssignmentsListProps) => {
  const isDateChange =
    request.requestData?.__typename === 'RosterRequestDataDateChange';

  return (
    <div>
      <h2 className="mb-5 text-lg text-primaryGray">Roster Assignments</h2>

      {(duplicateRequests.length > 0 || duplicateRequestsError) && (
        <div className="mb-5">
          <DuplicateChangeRequestWarning
            duplicateRequests={duplicateRequests}
            error={duplicateRequestsError}
          />
        </div>
      )}

      {request.rosterRoomAssignments?.map((assignment, index) => {
        const { checkInDate, checkOutDate } =
          (request.requestData as RosterRequestDataDateChange) ?? {};

        return (
          <div key={assignment.id}>
            <div className="mb-5 grid grid-cols-12 rounded-md border bg-white p-5">
              <div className="col-span-12">
                <div className="flex flex-col justify-start">
                  <div className="flex-1">
                    <span className="text-base">
                      {`${index + 1}. ${assignment.guest.lastName}, ${
                        assignment.guest.firstName
                      }`}
                    </span>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-primaryGray">
                      {`${startCase(
                        assignment.hotel.name.toLowerCase(),
                      )} - ${startCase(
                        assignment.hotel.address.city?.toLowerCase(),
                      )},
                        ${assignment.hotel.address.state}`}
                    </span>
                  </div>
                  {!isDateChange && (
                    <div className="flex-1">
                      <span className="text-sm text-primaryGray">
                        {`Check In: ${format(
                          parseISO(assignment.checkInDate),
                          'M/d/yyyy',
                        )} →${' '}
                        Check Out: ${format(
                          parseISO(assignment.checkOutDate),
                          'M/d/yyyy',
                        )}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {isDateChange && (
                <div className="col-span-12 col-start-1">
                  <AssignmentChanges
                    requestData={request.requestData}
                    assignment={assignment as RosterRoomAssignment}
                  />
                  {/* duplicate stay warning */}
                  <OverlappingStayWarning
                    className="mb-5"
                    newStayInfo={{
                      guestId: assignment.guest.id,
                      checkInDate,
                      checkOutDate,
                    }}
                    rosterItem={assignment}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AssignmentsList;
